import React, { useCallback, useEffect, useRef, useState } from "react";
import { Drawer, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { FiFolder } from "react-icons/fi";
import { toast } from "react-toastify";
import Spinner from "../../components/Spinner";

const backend_url = process.env.REACT_APP_BACKEND_SERVICE_URL;
// const backend_url = "https://datagenx-backend-srv-801923314065.europe-west3.run.app";

const PLATFORMS = ["Fivetran", "HVR6", "dbt labs", "Informatica", "Airbyte"];

async function fetchFileEntriesList(backendUrl, userId, platformId, signal) {
  const url = `${backendUrl}/list_files?user_id=${encodeURIComponent(
    userId,
  )}&platform_id=${encodeURIComponent(platformId)}`;
  const res = await fetch(url, signal ? { signal } : undefined);
  const data = await res.json().catch(() => ({}));
  const raw =
    data && typeof data.files === "object" && data.files !== null
      ? data.files
      : {};
  const entries = Object.entries(raw).map(([id, name]) => ({
    id,
    name: String(name ?? ""),
  }));
  return { res, data, entries };
}

function FileManager({ userId, selectedPlatform }) {
  const [open, setOpen] = useState(false);
  const [activePlatform, setActivePlatform] = useState(selectedPlatform);
  const [filesEntries, setFilesEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deletingFileIds, setDeletingFileIds] = useState([]);

  const fetchContextRef = useRef({ userId, activePlatform, open });
  fetchContextRef.current = { userId, activePlatform, open };

  const deleteInFlightRef = useRef(new Set());

  const refreshFileList = useCallback(async () => {
    const {
      userId: uid,
      activePlatform: plat,
      open: isOpen,
    } = fetchContextRef.current;
    if (!uid || !plat || !isOpen) return;
    try {
      const { res, data, entries } = await fetchFileEntriesList(
        backend_url,
        uid,
        plat,
      );
      const ctx = fetchContextRef.current;
      if (ctx.userId !== uid || ctx.activePlatform !== plat || !ctx.open) {
        return;
      }
      if (!res.ok) {
        return;
      }
      setFilesEntries(entries);
      setError(null);
    } catch {
      /* ignore background refresh errors */
    }
  }, []);

  useEffect(() => {
    if (open) {
      setActivePlatform(selectedPlatform);
    }
  }, [open, selectedPlatform]);

  useEffect(() => {
    if (!open) {
      setLoading(false);
      return;
    }

    setFilesEntries([]);
    setError(null);
    setLoading(true);

    if (!userId || !activePlatform) {
      setLoading(false);
      return;
    }

    const ac = new AbortController();

    (async () => {
      try {
        const { res, data, entries } = await fetchFileEntriesList(
          backend_url,
          userId,
          activePlatform,
          ac.signal,
        );
        if (ac.signal.aborted) return;

        if (!res.ok) {
          const msg =
            (typeof data?.message === "string" && data.message) ||
            (typeof data?.error === "string" && data.error) ||
            `Could not load files (${res.status}).`;
          setFilesEntries([]);
          setError(msg);
          return;
        }

        setFilesEntries(entries);
        setError(null);
      } catch (e) {
        if (e?.name === "AbortError" || ac.signal.aborted) return;
        setFilesEntries([]);
        setError(
          e?.message && typeof e.message === "string"
            ? e.message
            : "Failed to load files. Check your connection and try again.",
        );
      } finally {
        if (!ac.signal.aborted) {
          setLoading(false);
        }
      }
    })();

    return () => ac.abort();
  }, [open, activePlatform, userId]);

  const handleDeleteFile = useCallback(
    async (fileId) => {
      if (deleteInFlightRef.current.has(fileId)) return;
      if (!userId || !activePlatform) return;
      deleteInFlightRef.current.add(fileId);
      setDeletingFileIds((prev) =>
        prev.includes(fileId) ? prev : [...prev, fileId],
      );
      try {
        const res = await fetch(`${backend_url}/delete_file`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: String(userId),
            platform_id: activePlatform,
            file_id: fileId,
          }),
        });
        const data = await res.json().catch(() => ({}));
        const apiOk = data.sucess === true || data.success === true;
        if (!res.ok || !apiOk) {
          toast.error("Error Removing File!");
          return;
        }
        await refreshFileList();
      } catch {
        toast.error("Error Removing File!");
      } finally {
        deleteInFlightRef.current.delete(fileId);
        setDeletingFileIds((prev) => prev.filter((x) => x !== fileId));
      }
    },
    [userId, activePlatform, refreshFileList],
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open file manager"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          padding: "6px 10px",
          borderRadius: "8px",
          border: "1px solid rgba(69, 42, 209, 0.35)",
          background:
            "linear-gradient(135deg, rgba(69, 42, 209, 0.12), rgba(113, 93, 218, 0.12), rgba(62, 223, 208, 0.12))",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
          color: "var(--color-primary)",
          fontSize: "13px",
          fontWeight: "600",
          cursor: "pointer",
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
          marginLeft: "8px",
        }}
        onMouseDown={(e) => {
          e.currentTarget.style.transform = "scale(0.98)";
        }}
        onMouseUp={(e) => {
          e.currentTarget.style.transform = "scale(1)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "scale(1)";
        }}
      >
        <FiFolder size={16} />
        Files
      </button>

      <Drawer
        anchor="right"
        open={open}
        hideBackdrop
        onClose={() => {}}
        ModalProps={{
          keepMounted: false,
          disableEnforceFocus: true,
          disableAutoFocus: true,
          disableScrollLock: true,
          disableEscapeKeyDown: true,
          componentsProps: {
            root: {
              style: { pointerEvents: "none" },
            },
          },
        }}
        PaperProps={{
          style: {
            width: "min(100vw - 24px, 500px)",
            maxWidth: "85%",
            background: "rgba(255, 255, 255, 0.88)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
            borderLeft: "1px solid rgba(255, 255, 255, 0.5)",
            boxShadow: "-8px 0 32px rgba(0, 0, 0, 0.12)",
            display: "flex",
            flexDirection: "column",
            pointerEvents: "auto",
          },
        }}
      >
        <div
          style={{
            padding: "16px 18px",
            borderBottom: "1px solid rgba(209, 213, 219, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: "17px",
              fontWeight: "700",
              background: "linear-gradient(135deg, #452ad1, #715dda, #3edfd0)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Recently Uploaded Files for {activePlatform}
          </h2>
          <IconButton
            type="button"
            size="small"
            aria-label="Close file manager"
            onClick={() => setOpen(false)}
            sx={{
              color: "var(--color-primary)",
              backgroundColor: "rgba(69, 42, 209, 0.1)",
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: "rgba(69, 42, 209, 0.18)",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        {/*
          <div style={{ padding: "12px 14px 8px" }}>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
              }}
            >
              {PLATFORMS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setActivePlatform(p)}
                  style={{
                    padding: "6px 12px",
                    borderRadius: "8px",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: activePlatform === p ? "600" : "500",
                    transition: "background 0.2s ease, color 0.2s ease",
                    ...(activePlatform === p
                      ? {
                          background:
                            "linear-gradient(135deg, #452ad1, #715dda, #3edfd0)",
                          color: "white",
                        }
                      : {
                          background:
                            "linear-gradient(135deg, rgba(69, 42, 209, 0.12), rgba(113, 93, 218, 0.12), rgba(62, 223, 208, 0.12))",
                          color: "rgba(132, 132, 132, 1)",
                        }),
                  }}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
          */}

        <div
          style={{
            flex: 1,
            minHeight: 0,
            padding: "24px 14px 20px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {loading ? (
            <div
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                padding: "32px",
              }}
            >
              <Spinner />
            </div>
          ) : error ? (
            <div
              role="alert"
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                textAlign: "center",
                padding: "24px 12px",
                color: "#b91c1c",
                fontSize: "14px",
                lineHeight: 1.5,
              }}
            >
              {error}
            </div>
          ) : filesEntries.length === 0 ? (
            <div
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                textAlign: "center",
                padding: "24px 12px",
                color: "rgba(132, 132, 132, 1)",
                fontSize: "14px",
                lineHeight: 1.5,
              }}
            >
              No files were found, or something went wrong!
            </div>
          ) : (
            <ul
              style={{
                listStyle: "none",
                margin: 0,
                padding: 0,
                overflowY: "auto",
                maxHeight: "calc(100vh - 137px)",
                scrollbarWidth: "thin",
                scrollbarColor: "var(--color-secondary) #f0f0f0",
              }}
            >
              {filesEntries.map(({ id, name }) => {
                const rowDeleting = deletingFileIds.includes(id);
                return (
                  <li
                    key={id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "10px",
                      padding: "10px 12px",
                      marginBottom: "6px",
                      borderRadius: "10px",
                      background: "rgba(255, 255, 255, 0.6)",
                      border: "1px solid rgba(209, 213, 219, 0.6)",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#374151",
                        wordBreak: "break-word",
                        flex: 1,
                      }}
                      title={name}
                    >
                      {name}
                    </span>

                    <IconButton
                      type="button"
                      size="small"
                      aria-label={
                        rowDeleting ? `Removing ${name}` : `Delete ${name}`
                      }
                      aria-busy={rowDeleting}
                      disabled={rowDeleting}
                      onClick={() => handleDeleteFile(id)}
                      sx={{
                        color: "#dc2626",
                        flexShrink: 0,
                        width: 34,
                        height: 34,
                        ...(rowDeleting && { opacity: 1 }),
                      }}
                    >
                      {rowDeleting ? (
                        <Spinner />
                      ) : (
                        <DeleteIcon fontSize="small" />
                      )}
                    </IconButton>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </Drawer>
    </>
  );
}

export default FileManager;
